import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ParamTable } from "./ParamTable";
import {
  DocsParamLocation,
  DocsTableRow,
  RequestPanelProps,
} from "../interfaces";
import { API_BASE_URL } from "../utils";

/** One colour per verb, so the method reads before the url does. */
const METHOD_CLASSES: Record<string, string> = {
  GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST: "bg-amber-50 text-amber-700 border-amber-200",
};

/** The four blocks of a request, in the order a client fills them in. */
const PARAM_GROUPS: { location: DocsParamLocation; translationKey: string }[] = [
  { location: "header", translationKey: "headers" },
  { location: "path", translationKey: "pathParams" },
  { location: "query", translationKey: "queryParams" },
  { location: "body", translationKey: "body" },
];

export const RequestPanel: FC<RequestPanelProps> = (props) => {
  const { endpoint } = props;
  const { t } = useTranslation();

  return (
    // YOSHI-10464: the request reads like the request tab of a client - the verb and the url on one
    // bar, and each block of parameters in its own table - instead of a curl line to decipher.
    <div className="w-full overflow-hidden rounded-md border border-gray-200">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <span
          className={`rounded border px-2 py-1 font-mono text-xs font-bold ${METHOD_CLASSES[endpoint.method]}`}
        >
          {endpoint.method}
        </span>
        <code className="min-w-0 break-all font-mono text-xs text-gray-700 sm:text-sm">
          <span className="text-gray-400">{API_BASE_URL}</span>
          {endpoint.path}
        </code>
      </div>
      <div className="space-y-6 px-4 py-4">
        {PARAM_GROUPS.map((group) => {
          const rows: DocsTableRow[] = endpoint.params
            .filter((param) => param.location === group.location)
            // YOSHI-10464: the parameter tables and the glossary share one table component, so a
            // parameter is laid out as a row of it here instead of the component knowing two shapes.
            .map((param): DocsTableRow => {
              return {
                id: `${param.location}-${param.name}`,
                name: param.name,
                type: param.type,
                descriptionKey: `docs.params.${param.translationKey}`,
                isRequired: param.isRequired,
              };
            });

          if (rows.length === 0) {
            return null;
          }

          return (
            <div key={group.location} className="space-y-2">
              <h5 className="text-xs font-bold uppercase tracking-wide text-gray-500">
                {t(`docs.common.${group.translationKey}`)}
              </h5>
              <ParamTable rows={rows} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
